import React, { memo, useCallback, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View, } from 'react-native';
import { ModalFlatList } from 'design-system/ModalScrollables';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import Modal from 'utils/packages/Modal';
import AccountCard from 'design-system/AccountCard';
import { SelectableAccountCard } from 'design-system/SelectableAccountCard';
import { selectGBAccountsWithPISPInfo } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
const keyExtractor = (item) => item.id.toString();
const SelectFundingAccountModalContent = ({ onSelected, onScroll, selectedAccountId, }) => {
    const navigation = useMainStackNavigation();
    const { accounts, hasSupported } = useAppSelector(selectGBAccountsWithPISPInfo);
    const onOpenLogins = useCallback(() => {
        SelectFundingAccountModal.hide();
        navigation.navigate('Logins');
    }, [navigation]);
    const onConnectAccount = useCallback(() => {
        SelectFundingAccountModal.hide();
        navigation.navigate('ChooseBank', { back: true, investments: false });
    }, [navigation]);
    const ListHeaderComponent = useMemo(() => (<View style={staticStyles.headerContainer}>
        <Text TextThin-14 Secondary centered>
          This account will be locked for future deposits and withdrawals. To change this later, please contact our
          support team.
        </Text>
      </View>), []);
    const renderItem = useCallback(({ item }) => {
        const isUnsupported = !item.pispBankId;
        if (!hasSupported) {
            return (<TouchableOpacity activeOpacity={0.5} style={staticStyles.disabledCard}>
            <AccountCard alt onCard data={item} white hideSpaceUserAvatar isMultipleSelect={false} hideActionRequiredText notSupportedText="Not supported" onOpenLogins={onOpenLogins}/>
          </TouchableOpacity>);
        }
        return (<SelectableAccountCard alt data={item} white hideSpaceUserAvatar isMultipleSelect={false} onPress={() => {
                SelectFundingAccountModal.hide();
                onSelected(item);
            }} type="single" isSelected={item.id === selectedAccountId} hideActionRequiredText disabled={isUnsupported} notSupportedText={isUnsupported ? 'Not supported' : undefined} onOpenLogins={onOpenLogins}/>);
    }, [hasSupported, onOpenLogins, onSelected, selectedAccountId]);
    const contentContainerStyle = useMarginBottom('paddingBottom', hasSupported ? HEIGHT_PLUS_PADDING - rem(16) : HEIGHT_PLUS_PADDING + rem(56 + 8) - rem(16));
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    return (<>
      <ModalFlatList style={style} data={accounts} onScroll={onScroll} renderItem={renderItem} scrollEventThrottle={16} keyExtractor={keyExtractor} extraData={accounts} ListHeaderComponent={ListHeaderComponent} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>
      {hasSupported ? (<Button brandReversed floating title="Connect another account" onPress={onConnectAccount}/>) : (<FloatingButtons onOpenLogins={onOpenLogins} onConnectAccount={onConnectAccount}/>)}
    </>);
};
const FloatingButtons = memo(({ onOpenLogins, onConnectAccount }) => {
    const bottom = useMarginBottom('bottom');
    return (<View style={[staticStyles.floatingContainer, bottom]}>
        <Button brandReversed title="Check my bank connections" onPress={onOpenLogins}/>
        <Spacer h={8}/>
        <Button brand title="Connect an account" onPress={onConnectAccount}/>
      </View>);
});
const SelectFundingAccountModal = {
    show: (props) => {
        Modal.show(<SelectFundingAccountModalContent {...props}/>, {
            containerStyle: staticStyles.modal,
            title: 'Select funding account',
            titleBottomBorder: true,
            scrollable: {
                scrollValue: props.scrollValue,
            },
            onDidHideCallback: props.onDidHideCallback,
        });
    },
    hide: Modal.hide,
};
const staticStyles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    floatingContainer: {
        position: 'absolute',
        left: rem(16),
        right: rem(16),
    },
    headerContainer: {
        paddingBottom: rem(8),
        paddingHorizontal: rem(16),
    },
    disabledCard: {
        opacity: 0.5,
    },
});
export default SelectFundingAccountModal;
